import React, { useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { apiClient } from '@/services/api';
import { usePageConfig } from '@/contexts/PageConfigContext';
import { ContentLayout } from '@/layouts/ContentLayout';
import Error404 from '@/components/Error404';
import { TournamentInfoCard, type TournamentStat } from '@/components/TournamentInfoCard';
import { isCompleted, isLive } from '@/constants/matchStatus';
import { needsMatchPolling } from '@/utils/matchPolling';
import { getDateLocale } from '@/utils/dateLocale';
import MatchOverviewTab from '@/pages/match-overview/OverviewTab';
import MatchTournamentsTab from '@/pages/match-overview/TournamentsTab';
import type { MatchOverview, MatchOverviewMatch } from '@/types/matchOverview';
import { MatchOverviewContext } from './context';

/** Same cadence as the `/matches` day list and the backend's upstream poll. */
const POLL_INTERVAL_MS = 60_000;

/** Poll while the match is in play or about to kick off; stay quiet otherwise. */
function needsPolling(match: MatchOverviewMatch | undefined): boolean {
  return !!match && needsMatchPolling([match]);
}

/**
 * Standalone match page (`/match/:matchId/*`) — the match outside any
 * tournament, reached from the `/matches` day list.
 *
 * Tabs: Overview (default) | Tournaments — the array order is the mobile swipe
 * order and must stay in sync with the child <Route> list in App.tsx.
 */
const MatchOverviewLayout: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const { t } = useTranslation(['match', 'common']);

  const { data, isLoading, error } = useQuery<MatchOverview>({
    queryKey: ['match-overview', matchId],
    queryFn: async () => (await apiClient.getMatchOverview(matchId!)).data.data,
    enabled: !!matchId,
    refetchInterval: (q) => (needsPolling(q.state.data?.match) ? POLL_INTERVAL_MS : false),
    refetchIntervalInBackground: false,
  });

  const errorStatus = (error as { response?: { status?: number } } | null)?.response?.status;
  const isNotFound = errorStatus === 404 || errorStatus === 400;

  // Unknown match: the tab strip would only lead to more 404s — drop it (same
  // as the tournament-scoped match page); back stays available either way.
  const { setConfig } = usePageConfig();
  useLayoutEffect(() => {
    setConfig({
      title: isNotFound ? t('common:notFound') : t('match:title'),
      headerSolid: true,
      ...(isNotFound ? { showNavigation: false } : {}),
    });
  }, [isNotFound, setConfig, t]);

  const tabs = React.useMemo(() => ([
    { component: MatchOverviewTab, path: `/match/${matchId}` },
    { component: MatchTournamentsTab, path: `/match/${matchId}/tournaments` },
  ]), [matchId]);

  if (isLoading) return null;

  if (error) {
    return isNotFound ? <Error404 /> : null;
  }

  if (!data || !matchId) return null;

  const { match, league } = data;
  const kickoff = new Date(match.match_date);
  const hasScore = (isLive(match.status) || isCompleted(match.status))
    && match.score_team1 !== null && match.score_team2 !== null;

  const stats: TournamentStat[] = [
    {
      label: t(`match:status.${match.status}`, { defaultValue: match.status }),
      value: format(kickoff, 'd MMM, HH:mm', { locale: getDateLocale() }),
    },
  ];
  if (hasScore) {
    stats.push({ label: t('match:score'), value: `${match.score_team1} : ${match.score_team2}` });
  }

  const sidebar = (
    <TournamentInfoCard
      logoUrl={league.logoUrl || undefined}
      logoAlt={league.name}
      title={league.name}
      subtitle={[league.country, league.season].filter(Boolean).join(' · ')}
      subtitleTo={`/matches/${league.id}`}
      stats={stats}
      statLayout="row"
      collapsibleOnMobile
    />
  );

  return (
    <MatchOverviewContext.Provider value={{ match, league, matchId }}>
      <ContentLayout sidebar={sidebar} tabs={tabs} />
    </MatchOverviewContext.Provider>
  );
};

export default MatchOverviewLayout;
